import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import StatsCard from '../../components/ui/StatsCard';
import useTranslation from '../../hooks/useTranslation';
import { projectService } from '../../services/projectService';
import { formatCurrency } from '../../utils/formatCurrency';
import { supabase } from '../../lib/supabase';
import { computeDpTotals } from '../../utils/dpFinancials';
import { pdf } from '@react-pdf/renderer';
import PaymentRequestDocument from '../../pdf/PaymentRequestDocument';
import { useLanguage } from '../../contexts/LanguageContext';
import ExcelJS from 'exceljs';

import paymentRequestService from '../../services/paymentRequestService';
import organizationService from '../../services/organizationService';
import { getCurrentOrgId } from '../../lib/getCurrentOrgId';
import { listProfessionalsForProject, listClientsForProject } from '../../services/projectDirectoryService';
import PaymentRequestsTable from './components/PaymentRequestsTable';
import PaymentRequestModal from './components/PaymentRequestModal';
import DocumentsModal from './components/DocumentsModal';
import PaymentRequestViewModal from './components/PaymentRequestViewModal';

// ─── Status labels (mirrors getStatusConfig in PaymentRequestsTable) ──────────
const getPaymentRequestStatusLabels = (t) => ({
  draft:     t('paymentRequests.status.draft')     || 'Draft',
  submitted: t('paymentRequests.status.submitted') || 'Submitted',
  approved:  t('paymentRequests.status.approved')  || 'Approved',
});

// ─── Filename sanitizer ───────────────────────────────────────────────────────
const sanitizeFilename = (str) => (str || '')?.replace(/[^A-Za-z0-9_-]/g, '_');

// ─── Export column headers (shared by CSV / Excel / PDF) ─────────────────────
const EXPORT_COLUMNS = (t) => [
  t('paymentRequests.export.columns.requestNumber'),
  t('paymentRequests.export.columns.date'),
  t('paymentRequests.export.columns.total'),
  t('paymentRequests.export.columns.status'),
];

// ─── Excel Export ─────────────────────────────────────────────────────────────
const exportPaymentRequestsToExcel = async (rows, projectName, label, t, language) => {
  const STATUS_LABELS = getPaymentRequestStatusLabels(t);
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)?.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA');
  };

  const grandTotal = rows?.reduce(
    (s, row) => s + (row?.totalAmount != null ? parseFloat(row?.totalAmount) || 0 : 0),
    0
  );

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'OnsitePM';
  workbook.created = new Date();

  const sheet = workbook?.addWorksheet(t('paymentRequests.title'));

  // ── Column widths ──────────────────────────────────────────────────────────
  sheet.columns = [
    { key: 'number', width: 16 },
    { key: 'date',   width: 18 },
    { key: 'total',  width: 22 },
    { key: 'status', width: 20 },
  ];

  // ── Title row ──────────────────────────────────────────────────────────────
  const titleRow = sheet?.addRow([t('paymentRequests.title'), '', '', '']);
  titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FF111111' } };
  sheet?.mergeCells(`A${titleRow?.number}:D${titleRow?.number}`);

  // ── Subtitle row ──────────────────────────────────────────────────────────
  const exportDate = new Date()?.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', { month: 'long', day: 'numeric', year: 'numeric' });
  const subtitleRow = sheet?.addRow([`${projectName || t('paymentRequests.export.projectFallback')} — ${t('paymentRequests.export.exportedOn', { date: exportDate })}`, '', '', '']);
  subtitleRow.getCell(1).font = { size: 10, color: { argb: 'FF666666' } };
  sheet?.mergeCells(`A${subtitleRow?.number}:D${subtitleRow?.number}`);

  // ── Blank spacer ──────────────────────────────────────────────────────────
  sheet?.addRow([]);

  // ── Header row ────────────────────────────────────────────────────────────
  const headerRow = sheet?.addRow(EXPORT_COLUMNS(t));
  headerRow?.eachCell((cell) => {
    cell.font = { bold: true, size: 10, color: { argb: 'FF374151' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
    cell.border = {
      top:    { style: 'thin', color: { argb: 'FFE5E7EB' } },
      bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      left:   { style: 'thin', color: { argb: 'FFE5E7EB' } },
      right:  { style: 'thin', color: { argb: 'FFE5E7EB' } },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  });
  // Right-align Total header
  headerRow.getCell(3).alignment = { vertical: 'middle', horizontal: 'right' };

  // ── Data rows ─────────────────────────────────────────────────────────────
  rows?.forEach((row) => {
    const statusLabel = STATUS_LABELS?.[row?.status] || row?.status || '';
    const totalVal = row?.totalAmount != null ? parseFloat(row?.totalAmount) || 0 : null;

    const dataRow = sheet?.addRow([
      `DP${row?.number ?? '?'}`,
      formatDate(row?.created_at),
      totalVal,
      statusLabel,
    ]);

    // Number format for Total cell
    const totalCell = dataRow?.getCell(3);
    if (totalVal != null) {
      totalCell.numFmt = '"$"#,##0.00';
    } else {
      totalCell.value = '—';
    }
    totalCell.alignment = { horizontal: 'right' };

    // Light bottom border on each data row
    dataRow?.eachCell((cell) => {
      cell.border = {
        bottom: { style: 'hair', color: { argb: 'FFF3F4F6' } },
      };
      cell.alignment = { vertical: 'middle', ...(cell?.alignment || {}) };
    });
  });

  // ── Totals footer row ─────────────────────────────────────────────────────
  const footerRow = sheet?.addRow([
    `Total (${rows?.length} items)`,
    '',
    grandTotal,
    '',
  ]);
  footerRow.getCell(1).font = { bold: true, size: 10 };
  footerRow.getCell(3).font = { bold: true, size: 10 };
  footerRow.getCell(3).numFmt = '"$"#,##0.00';
  footerRow.getCell(3).alignment = { horizontal: 'right' };
  footerRow?.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
    cell.border = {
      top:    { style: 'medium', color: { argb: 'FFE5E7EB' } },
      bottom: { style: 'thin',   color: { argb: 'FFE5E7EB' } },
    };
  });

  // ── Row height for data rows ───────────────────────────────────────────────
  sheet?.eachRow((r) => { r.height = 18; });

  // ── Generate & download ───────────────────────────────────────────────────
  const buffer = await workbook?.xlsx?.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeName = sanitizeFilename(projectName || 'Projet');
  a.download = `${safeName}_demandes_paiement${label ? '_' + sanitizeFilename(label) : ''}_${new Date()?.toISOString()?.slice(0, 10)}.xlsx`;
  document?.body?.appendChild(a);
  a?.click();
  document?.body?.removeChild(a);
  URL.revokeObjectURL(url);
};

// ─── CSV Export ───────────────────────────────────────────────────────────────
const exportPaymentRequestsToCSV = (rows, projectName, label, t, language) => {
  const STATUS_LABELS = getPaymentRequestStatusLabels(t);
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)?.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA');
  };

  const headers = EXPORT_COLUMNS(t);
  const csvRows = [
    headers?.map(h => `"${h}"`)?.join(','),
    ...rows?.map(row => [
      `"DP${row?.number ?? '?'}"`,
      `"${formatDate(row?.created_at)}"`,
      `"${row?.totalAmount != null ? formatCurrency(row?.totalAmount) : '—'}"`,
      `"${(STATUS_LABELS?.[row?.status] || row?.status || '')?.replace(/"/g, '""')}"`
    ]?.join(','))
  ];

  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeName = sanitizeFilename(projectName || 'Projet');
  a.download = `${safeName}_demandes_paiement${label ? '_' + sanitizeFilename(label) : ''}_${new Date()?.toISOString()?.slice(0, 10)}.csv`;
  document?.body?.appendChild(a);
  a?.click();
  document?.body?.removeChild(a);
  URL.revokeObjectURL(url);
};

// ─── PDF Export ───────────────────────────────────────────────────────────────
const exportPaymentRequestsToPDF = (rows, projectName, label, t, language) => {
  const STATUS_LABELS = getPaymentRequestStatusLabels(t);
  const STATUS_CONFIG = {
    draft:     { bg: '#f3f4f6', color: '#4b5563' },
    submitted: { bg: '#dbeafe', color: '#1d4ed8' },
    approved:  { bg: '#dcfce7', color: '#16a34a' },
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)?.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA');
  };

  const tableRows = rows?.map(row => {
    const statusLabel = STATUS_LABELS?.[row?.status] || row?.status || '';
    const cfg = STATUS_CONFIG?.[row?.status] || { bg: '#f3f4f6', color: '#4b5563' };
    const badge = `<span style="display:inline-block;padding:2px 8px;border-radius:9999px;font-size:10px;font-weight:600;background:${cfg?.bg};color:${cfg?.color}">${statusLabel}</span>`;
    const totalDisplay = row?.totalAmount != null ? formatCurrency(row?.totalAmount) : '—';
    return `
    <tr>
      <td>DP${row?.number ?? '?'}</td>
      <td>${formatDate(row?.created_at)}</td>
      <td style="text-align:right">${totalDisplay}</td>
      <td>${badge}</td>
    </tr>
  `;
  })?.join('');

  const grandTotal = rows?.reduce((s, row) => s + (row?.totalAmount != null ? parseFloat(row?.totalAmount) || 0 : 0), 0);

  const title = t('paymentRequests.title');
  const subtitle = projectName || t('paymentRequests.export.projectFallback');
  const [colNumber, colDate, colTotal, colStatus] = EXPORT_COLUMNS(t);
  const exportedOn = t('paymentRequests.export.exportedOn', {
    date: new Date()?.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', { month: 'long', day: 'numeric', year: 'numeric' })
  });

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 11px; color: #111; margin: 32px; }
    h1 { font-size: 16px; margin-bottom: 4px; }
    .sub { color: #666; font-size: 11px; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #f3f4f6; text-align: left; padding: 6px 10px; font-size: 10px; color: #555; border-bottom: 1px solid #e5e7eb; }
    td { padding: 6px 10px; border-bottom: 1px solid #f3f4f6; }
    tfoot td { font-weight: bold; background: #f9fafb; border-top: 2px solid #e5e7eb; }
  </style>
</head>
<body>
  <h1>${title}</h1>
  <div class="sub">${subtitle} &mdash; ${exportedOn}</div>
  <table>
    <thead>
      <tr>
        <th>${colNumber}</th>
        <th>${colDate}</th>
        <th style="text-align:right">${colTotal}</th>
        <th>${colStatus}</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
    <tfoot>
      <tr>
        <td colspan="2">${t('paymentRequests.export.totalItems', { count: rows?.length })}</td>
        <td style="text-align:right">${formatCurrency(grandTotal)}</td>
        <td></td>
      </tr>
    </tfoot>
  </table>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (win) {
    win?.document?.write(html);
    win?.document?.close();
    win?.focus();
    setTimeout(() => { win?.print(); }, 400);
  }
};

// ─── Excel Detail Export (single request) ────────────────────────────────────
const exportDetailToExcel = async ({
  req,
  allItems,
  values,
  projectName,
  projectNumber,
  clientName,
  companyName,
  orgAddress,
  orgPhone,
  orgTpsNumber,
  orgTvqNumber,
  language,
  t,
}) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)?.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA');
  };

  const valueMap = {};
  values?.forEach(v => { valueMap[v?.item_id] = v; });

  const enrichedItems = allItems?.map(item => {
    const val = valueMap?.[item?.id];
    return {
      description:    item?.description || '',
      contractAmount: parseFloat(item?.contract_amount) || 0,
      previousAmount: parseFloat(val?.previous_amount) || 0,
      currentAmount:  parseFloat(val?.current_amount) || 0,
      monthlyAmount:  parseFloat(val?.monthly_amount) || 0,
      isExtra:        item?.is_extra || false,
      extraCode:      item?.extra_code || null,
    };
  });

  const baseItems  = enrichedItems?.filter(i => !i?.isExtra);
  const extraItems = enrichedItems?.filter(i => i?.isExtra);

  let subtotal = enrichedItems?.reduce((s, i) => s + (i?.monthlyAmount || 0), 0);
  const { retention, avantTaxes: montantSansTaxes, tps, tvq, totalAvecTaxes: total } = computeDpTotals(subtotal, req?.retention_rate ?? 10);

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'OnsitePM';
  workbook.created = new Date();

  const sheet = workbook?.addWorksheet(t('paymentRequests.excelDetail.sheetName'));

  // ── Column widths ──────────────────────────────────────────────────────────
  sheet.columns = [
    { key: 'description', width: 42 },
    { key: 'contract',    width: 18 },
    { key: 'previous',    width: 18 },
    { key: 'current',     width: 18 },
    { key: 'monthly',     width: 18 },
  ];

  const CURRENCY_FMT = '"$"#,##0.00';
  const HEADER_FILL  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
  const SECTION_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEF2FF' } };
  const FOOTER_FILL  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
  const THIN_BORDER  = { style: 'thin', color: { argb: 'FFE5E7EB' } };
  const HAIR_BORDER  = { style: 'hair', color: { argb: 'FFF3F4F6' } };

  const addBorderRow = (row) => {
    row?.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = { bottom: HAIR_BORDER };
      cell.alignment = { vertical: 'middle', ...(cell?.alignment || {}) };
    });
  };

  // ── Title ──────────────────────────────────────────────────────────────────
  const titleLabel = t('paymentRequests.pdf.title');
  const titleRow = sheet?.addRow([`${titleLabel} — DP${req?.number ?? '?'}`, '', '', '', '']);
  titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FF111111' } };
  sheet?.mergeCells(`A${titleRow?.number}:E${titleRow?.number}`);
  titleRow.height = 22;

  // ── Project / client info ──────────────────────────────────────────────────
  const infoRows = [
    [t('paymentRequests.excelDetail.projectLabel'), `${projectNumber || ''}${projectNumber && projectName ? ' — ' : ''}${projectName || ''}`],
    [t('paymentRequests.excelDetail.clientLabel'), clientName || '—'],
    [t('paymentRequests.excelDetail.contractorLabel'), companyName || ''],
  ];
  if (req?.period_start || req?.period_end) {
    const ps = req?.period_start ? formatDate(req?.period_start) : '';
    const pe = req?.period_end   ? formatDate(req?.period_end)   : '';
    infoRows?.push([t('paymentRequests.excelDetail.periodLabel'), ps && pe ? `${ps} → ${pe}` : ps || pe]);
  }
  infoRows?.forEach(([label, value]) => {
    const r = sheet?.addRow([label, value, '', '', '']);
    r.getCell(1).font = { bold: true, size: 9, color: { argb: 'FF6B7280' } };
    r.getCell(2).font = { size: 9, color: { argb: 'FF111111' } };
    sheet?.mergeCells(`B${r?.number}:E${r?.number}`);
    r.height = 15;
  });

  sheet?.addRow([]); // spacer

  // ── Helper: add section header ─────────────────────────────────────────────
  const addSectionHeader = (label) => {
    const r = sheet?.addRow([label, '', '', '', '']);
    r.getCell(1).font = { bold: true, size: 10, color: { argb: 'FF374151' } };
    r.getCell(1).fill = SECTION_FILL;
    sheet?.mergeCells(`A${r?.number}:E${r?.number}`);
    r.height = 16;
  };

  // ── Helper: add column header row ─────────────────────────────────────────
  const addColumnHeaders = () => {
    const labels = [
      t('paymentRequests.excelDetail.columns.description'),
      t('paymentRequests.excelDetail.columns.contractAmount'),
      t('paymentRequests.excelDetail.columns.previous'),
      t('paymentRequests.excelDetail.columns.thisRequest'),
      t('paymentRequests.excelDetail.columns.monthly'),
    ];
    const r = sheet?.addRow(labels);
    r?.eachCell((cell) => {
      cell.font = { bold: true, size: 9, color: { argb: 'FF374151' } };
      cell.fill = HEADER_FILL;
      cell.border = { top: THIN_BORDER, bottom: THIN_BORDER, left: THIN_BORDER, right: THIN_BORDER };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
    });
    // Right-align numeric headers
    [2, 3, 4, 5]?.forEach(col => {
      r.getCell(col).alignment = { vertical: 'middle', horizontal: 'right' };
    });
    r.height = 16;
    return r;
  };

  // ── Helper: add item row ───────────────────────────────────────────────────
  const addItemRow = (item) => {
    const r = sheet?.addRow([
      item?.description,
      item?.contractAmount || null,
      item?.previousAmount || null,
      item?.currentAmount  || null,
      item?.monthlyAmount  || null,
    ]);
    [2, 3, 4, 5]?.forEach(col => {
      const cell = r?.getCell(col);
      cell.numFmt = CURRENCY_FMT;
      cell.alignment = { horizontal: 'right', vertical: 'middle' };
    });
    r.getCell(1).alignment = { vertical: 'middle', wrapText: false };
    addBorderRow(r);
    r.height = 16;
  };

  // ── Base contract lines ────────────────────────────────────────────────────
  if (baseItems?.length > 0) {
    addSectionHeader(t('paymentRequests.excelDetail.contractLines'));
    addColumnHeaders();
    baseItems?.forEach(addItemRow);
    sheet?.addRow([]); // spacer
  }

  // ── Extras ────────────────────────────────────────────────────────────────
  if (extraItems?.length > 0) {
    addSectionHeader(t('paymentRequests.excelDetail.extras'));
    addColumnHeaders();
    extraItems?.forEach(addItemRow);
    sheet?.addRow([]); // spacer
  }

  // ── Summary / totals ──────────────────────────────────────────────────────
  const summaryLabel = t('paymentRequests.excelDetail.summary');
  const summaryRow = sheet?.addRow([summaryLabel, '', '', '', '']);
  summaryRow.getCell(1).font = { bold: true, size: 10, color: { argb: 'FF374151' } };
  summaryRow.getCell(1).fill = SECTION_FILL;
  sheet?.mergeCells(`A${summaryRow?.number}:E${summaryRow?.number}`);
  summaryRow.height = 16;

  const addSummaryLine = (label, value, bold = false) => {
    const r = sheet?.addRow(['', '', '', label, value]);
    r.getCell(4).font = { bold, size: 9, color: { argb: 'FF374151' } };
    r.getCell(4).alignment = { horizontal: 'right', vertical: 'middle' };
    r.getCell(5).numFmt = CURRENCY_FMT;
    r.getCell(5).font = { bold, size: 9 };
    r.getCell(5).alignment = { horizontal: 'right', vertical: 'middle' };
    r.getCell(5).fill = bold ? FOOTER_FILL : undefined;
    if (bold) {
      r.getCell(5).border = { top: THIN_BORDER };
    }
    r.height = 15;
  };

  addSummaryLine(t('paymentRequests.pdf.totals.subtotal'), subtotal);
  addSummaryLine(t('paymentRequests.pdf.totals.holdback'), -retention);
  addSummaryLine(t('paymentRequests.pdf.totals.subtotalAfterHoldback'), montantSansTaxes);
  addSummaryLine(t('paymentRequests.pdf.totals.tps'), tps);
  addSummaryLine(t('paymentRequests.pdf.totals.tvq'), tvq);
  addSummaryLine(t('paymentRequests.excelDetail.total'), total, true);

  // ── Row heights for all rows ───────────────────────────────────────────────
  sheet?.eachRow((r) => { if (!r?.height) r.height = 15; });

  // ── Generate & download ───────────────────────────────────────────────────
  const buffer = await workbook?.xlsx?.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeProject = sanitizeFilename(projectNumber || projectName || 'Projet');
  const today = new Date()?.toISOString()?.slice(0, 10);
  a.download = `${safeProject}_DP${req?.number ?? '?'}_${today}.xlsx`;
  document?.body?.appendChild(a);
  a?.click();
  document?.body?.removeChild(a);
  URL.revokeObjectURL(url);
};

const ClientInvoicesPage = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { language } = useLanguage();

  const initialProject = location?.state?.project;
  const [project, setProject] = useState(initialProject || null);

  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingRequest, setDeletingRequest] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [showDeleteBlocked, setShowDeleteBlocked] = useState(false);
  const [blockedDeleteCounts, setBlockedDeleteCounts] = useState({ invoices: 0, expenses: 0 });
  const [blockedDeleteRequest, setBlockedDeleteRequest] = useState(null);
  const [deleteBlockedMessage, setDeleteBlockedMessage] = useState('');
  const [showEditBlocked, setShowEditBlocked] = useState(false);
  const [editBlockedMessage, setEditBlockedMessage] = useState('');
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [docsRequest, setDocsRequest] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBlockedCreate, setShowBlockedCreate] = useState(false);
  const [blockedByRequest, setBlockedByRequest] = useState(null);
  const [savingRequest, setSavingRequest] = useState(false);

  // ─── View (read-only aperçu) state ────────────────────────────────────────
  const [viewingRequest, setViewingRequest] = useState(null);
  const [viewPdfExporting, setViewPdfExporting] = useState(false);

  // ─── Selection state ───────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [downloadingDetail, setDownloadingDetail] = useState(false);

  // ─── Approve request gate state ───────────────────────────────────────────
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvingRequest, setApprovingRequest] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [signedPdfFile, setSignedPdfFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [downloadingUnsigned, setDownloadingUnsigned] = useState(false);

  // ─── Move filteredRequests declaration before handleToggleAll and handleDownloadDetailPDF ───
  const filteredRequests = searchQuery?.trim()
    ? paymentRequests?.filter(r => {
        const q = searchQuery?.toLowerCase()?.trim();
        const num = `dp${r?.number}`?.toLowerCase();
        const status = r?.status?.toLowerCase() || '';
        return num?.includes(q) || status?.includes(q);
      })
    : paymentRequests;

  const handleToggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id);
      else next?.add(id);
      return next;
    });
  };

  const handleToggleAll = () => {
    if (selectedIds?.size === filteredRequests?.length && filteredRequests?.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredRequests?.map(r => r?.id)));
    }
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  // ─── Logo helper for public bucket ────────────────────────────────────────
  const fetchLogoAsBase64 = async (storagePath) => {
    try {
      const { data, error } = await supabase?.storage
        ?.from('company-assets')
        ?.createSignedUrl(storagePath, 60);
      if (error || !data?.signedUrl) return null;
      const response = await fetch(data?.signedUrl);
      if (!response?.ok) return null;
      const blob = await response?.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader?.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  // ─── Fetch org logo from public organization-logos bucket ─────────────────
  const fetchOrgLogoAsBase64 = async (publicUrl) => {
    try {
      const response = await fetch(publicUrl);
      if (!response?.ok) return null;
      const blob = await response?.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader?.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  // ─── Detailed PDF download handler ────────────────────────────────────────
  const handleDownloadDetailPDF = async () => {
    if (selectedIds?.size === 0 || downloadingDetail) return;
    setDownloadingDetail(true);
    try {
      // Fetch full org info ONCE (includes logo_url + all new fields)
      let companyName = '';
      let logoUrl = null;
      let orgTagline = null;
      let orgRbqNumber = null;
      let orgTpsNumber = null;
      let orgTvqNumber = null;
      let orgEmail = null;
      let orgWebsite = null;
      let orgAddress = null;
      let orgPhone = null;

      try {
        const orgId = await getCurrentOrgId();
        if (orgId) {
          const { data: org } = await organizationService?.getOrgInfo(orgId);
          if (org) {
            companyName   = org?.name || '';
            orgTagline    = org?.tagline || null;
            orgRbqNumber  = org?.rbq_number || null;
            orgTpsNumber  = org?.tps_number || null;
            orgTvqNumber  = org?.tvq_number || null;
            orgEmail      = org?.email || null;
            orgWebsite    = org?.website || null;
            orgAddress    = org?.address || null;
            orgPhone      = org?.phone || null;

            // Fetch logo from public organization-logos bucket
            if (org?.logo_url) {
              const { data: urlData } = supabase?.storage
                ?.from('organization-logos')
                ?.getPublicUrl(org?.logo_url);
              if (urlData?.publicUrl) {
                const cacheBustedUrl = `${urlData?.publicUrl}?v=${Date.now()}`;
                logoUrl = await fetchOrgLogoAsBase64(cacheBustedUrl);
              }
            }
          }
        }
      } catch {
        // continue without org info
      }

      // Fetch professionals and clients for the project once
      let professionals = [];
      let clients = [];
      try {
        professionals = await listProfessionalsForProject(projectId);
      } catch {
        professionals = [];
      }
      try {
        clients = await listClientsForProject(projectId);
      } catch {
        clients = [];
      }

      const projectNumber = project?.projectNumber || project?.project_number || '';
      const projectName   = project?.name || '';
      const clientName    = project?.client_name || '—';
      const clientProjectNumber = project?.clientProjectNumber || project?.client_project_number || null;

      // Compose project installation address
      const projectAddress = [
        project?.address,
        project?.city,
        project?.state,
        project?.zipCode || project?.zip_code,
      ]?.filter(Boolean)?.join(', ') || null;

      // PM full name from project.pm join
      const pmFullName = project?.pm?.fullName || project?.pm?.full_name || null;

      // Fetch all items for the project once
      let allItems = [];
      try {
        allItems = await paymentRequestService?.getItemsByProjectId(projectId);
      } catch {
        allItems = [];
      }

      // Loop over selected requests in display order
      const selectedRequests = filteredRequests?.filter(r => selectedIds?.has(r?.id));
      const today = new Date()?.toISOString()?.slice(0, 10);

      for (const req of selectedRequests) {
        try {
          const values = await paymentRequestService?.getValuesByRequestId(req?.id);
          const valueMap = {};
          values?.forEach(v => { valueMap[v?.item_id] = v; });

          const enrichedItems = allItems?.map(item => {
            const val = valueMap?.[item?.id];
            return {
              description:    item?.description || '',
              contractAmount: parseFloat(item?.contract_amount) || 0,
              previousAmount: parseFloat(val?.previous_amount) || 0,
              currentAmount:  parseFloat(val?.current_amount) || 0,
              monthlyAmount:  parseFloat(val?.monthly_amount) || 0,
              isExtra:        item?.is_extra || false,
              extraCode:      item?.extra_code || null,
            };
          });

          const blob = await pdf(
            <PaymentRequestDocument
              request={req}
              items={enrichedItems}
              projectName={projectName}
              projectNumber={projectNumber}
              clientName={clientName}
              companyName={companyName}
              logoUrl={logoUrl}
              language={language || 'fr'}
              periodStart={req?.period_start || null}
              periodEnd={req?.period_end || null}
              projectAddress={projectAddress}
              pmFullName={pmFullName}
              contractorAddress={orgAddress}
              contractorPhone={orgPhone}
              professionals={professionals}
              clients={clients}
              tagline={orgTagline}
              rbqNumber={orgRbqNumber}
              tpsNumber={orgTpsNumber}
              tvqNumber={orgTvqNumber}
              orgEmail={orgEmail}
              orgWebsite={orgWebsite}
              orgAddress={orgAddress}
              orgPhone={orgPhone}
              clientProjectNumber={clientProjectNumber}
              retentionRate={req?.retention_rate ?? 10}
            />
          )?.toBlob();

          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const safeProject = sanitizeFilename(projectNumber || projectName || 'Projet');
          a.download = `${safeProject}_DP${req?.number ?? '?'}_${today}.pdf`;
          document?.body?.appendChild(a);
          a?.click();
          document?.body?.removeChild(a);
          URL.revokeObjectURL(url);

          // 300ms delay to avoid browser blocking multi-download
          await new Promise(r => setTimeout(r, 300));
        } catch (err) {
          console.error(`PDF generation failed for DP${req?.number}:`, err);
        }
      }

      handleClearSelection();
    } finally {
      setDownloadingDetail(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchRequests();
      if (!initialProject) {
        projectService?.getById(projectId)
          ?.then(data => setProject(data))
          ?.catch(err => console.error('Error fetching project:', err));
      }
    }
  }, [projectId]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const data = await paymentRequestService?.getByProjectId(projectId);

      const enriched = await Promise.all(
        data?.map(async (req) => {
          try {
            const values = await paymentRequestService?.getValuesByRequestId(req?.id);
            let subtotal = 0;
            values?.forEach(v => { subtotal += parseFloat(v?.monthly_amount) || 0; });
            const { totalAvecTaxes: total, avantTaxes: montantSansTaxes } = computeDpTotals(subtotal, req?.retention_rate ?? 10);
            return { ...req, totalAmount: total, montantSansTaxes };
          } catch {
            return { ...req, totalAmount: null, montantSansTaxes: null };
          }
        })
      );

      setPaymentRequests(enriched);
    } catch (err) {
      console.error('Error fetching payment requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRequest = () => {
    // Determine the latest request by highest number (not array position)
    if (paymentRequests && paymentRequests?.length > 0) {
      const latest = paymentRequests?.reduce((prev, curr) =>
        (curr?.number ?? 0) > (prev?.number ?? 0) ? curr : prev
      );
      if (latest?.status === 'draft') {
        setBlockedByRequest(latest);
        setShowBlockedCreate(true);
        return;
      }
    }
    // Zero requests (first DP) or latest is submitted/approved → allow
    setEditingRequest(null);
    setShowModal(true);
  };

  const handleEditRequest = (req) => {
    if (req?.status === 'approved') {
      setDeleteBlockedMessage(t('paymentRequests.editBlockedApproved', { number: req?.number }));
      setShowDeleteBlocked(true);
      return;
    }
    setEditingRequest(req);
    setShowModal(true);
  };

  const handleDeleteRequest = async (req) => {
    try {
      // Step 1: Block if approved
      if (req?.status === 'approved') {
        setDeleteBlockedMessage(t('paymentRequests.deleteBlockedApproved', { number: req?.number }));
        setBlockedDeleteRequest(req);
        setShowDeleteBlocked(true);
        return;
      }

      // Step 2: Block if a newer DP exists
      const maxNumber = Math.max(...(paymentRequests?.map(r => r?.number) ?? [0]));
      if (req?.number < maxNumber) {
        setDeleteBlockedMessage(t('paymentRequests.deleteBlockedNewerExists', { number: req?.number, max: maxNumber }));
        setBlockedDeleteRequest(req);
        setShowDeleteBlocked(true);
        return;
      }

      // Step 3: Block if linked costs exist
      const { invoices, expenses } = await paymentRequestService?.getLinkedCostCounts(req?.id);
      if (invoices > 0 || expenses > 0) {
        setDeleteBlockedMessage(t('paymentRequests.deleteBlockedMessage', { invoices, expenses }));
        setBlockedDeleteCounts({ invoices, expenses });
        setBlockedDeleteRequest(req);
        setShowDeleteBlocked(true);
        return;
      }

      // Step 4: All checks passed — open confirm dialog
      setDeletingRequest(req);
      setDeleteError('');
      setShowDeleteConfirm(true);
    } catch (err) {
      console.error('Error checking linked costs:', err);
      // Fallback: open confirm dialog so user can still attempt delete
      setDeletingRequest(req);
      setDeleteError('');
      setShowDeleteConfirm(true);
    }
  };

  const confirmDelete = async () => {
    if (!deletingRequest) return;
    setDeleteLoading(true);
    try {
      await paymentRequestService?.delete(deletingRequest?.id);
      setPaymentRequests(prev => prev?.filter(r => r?.id !== deletingRequest?.id));
      setShowDeleteConfirm(false);
      setDeletingRequest(null);
    } catch (err) {
      console.error('Error deleting payment request:', err);
      setDeleteError(t('paymentRequests.deleteFailed'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleViewDocuments = (req) => {
    setDocsRequest(req);
    setShowDocsModal(true);
  };

  const handleViewRequest = (req) => {
    setViewingRequest(req);
  };

  // ─── Approve request: PDF download + upload + updateStatus ───────────────
  const generatePaymentRequestPdfBlob = async (req) => {
    let companyName = '';
    let logoUrl = null;
    let orgTagline = null;
    let orgRbqNumber = null;
    let orgTpsNumber = null;
    let orgTvqNumber = null;
    let orgEmail = null;
    let orgWebsite = null;
    let orgAddress = null;
    let orgPhone = null;

    try {
      const orgId = await getCurrentOrgId();
      if (orgId) {
        const { data: org } = await organizationService?.getOrgInfo(orgId);
        if (org) {
          companyName   = org?.name || '';
          orgTagline    = org?.tagline || null;
          orgRbqNumber  = org?.rbq_number || null;
          orgTpsNumber  = org?.tps_number || null;
          orgTvqNumber  = org?.tvq_number || null;
          orgEmail      = org?.email || null;
          orgWebsite    = org?.website || null;
          orgAddress    = org?.address || null;
          orgPhone      = org?.phone || null;
          if (org?.logo_url) {
            const { data: urlData } = supabase?.storage
              ?.from('organization-logos')
              ?.getPublicUrl(org?.logo_url);
            if (urlData?.publicUrl) {
              const cacheBustedUrl = `${urlData?.publicUrl}?v=${Date.now()}`;
              logoUrl = await fetchOrgLogoAsBase64(cacheBustedUrl);
            }
          }
        }
      }
    } catch { /* continue without org info */ }

    let professionals = [];
    let clients = [];
    try { professionals = await listProfessionalsForProject(projectId); } catch { professionals = []; }
    try { clients = await listClientsForProject(projectId); } catch { clients = []; }

    const projectNumber = project?.projectNumber || project?.project_number || '';
    const projectName   = project?.name || '';
    const clientName    = project?.client_name || '—';
    const clientProjectNumber = project?.clientProjectNumber || project?.client_project_number || null;
    const projectAddress = [project?.address, project?.city, project?.state, project?.zipCode || project?.zip_code]?.filter(Boolean)?.join(', ') || null;
    const pmFullName = project?.pm?.fullName || project?.pm?.full_name || null;

    let allItems = [];
    try { allItems = await paymentRequestService?.getItemsByProjectId(projectId); } catch { allItems = []; }

    const values = await paymentRequestService?.getValuesByRequestId(req?.id);
    const valueMap = {};
    values?.forEach(v => { valueMap[v?.item_id] = v; });

    const enrichedItems = allItems?.map(item => {
      const val = valueMap?.[item?.id];
      return {
        description:    item?.description || '',
        contractAmount: parseFloat(item?.contract_amount) || 0,
        previousAmount: parseFloat(val?.previous_amount) || 0,
        currentAmount:  parseFloat(val?.current_amount)  || 0,
        monthlyAmount:  parseFloat(val?.monthly_amount)  || 0,
        isExtra:        item?.is_extra || false,
        extraCode:      item?.extra_code || null,
      };
    });

    const blob = await pdf(
      <PaymentRequestDocument
        request={req}
        items={enrichedItems}
        projectName={projectName}
        projectNumber={projectNumber}
        clientName={clientName}
        companyName={companyName}
        logoUrl={logoUrl}
        language={language || 'fr'}
        periodStart={req?.period_start || null}
        periodEnd={req?.period_end || null}
        projectAddress={projectAddress}
        pmFullName={pmFullName}
        contractorAddress={orgAddress}
        contractorPhone={orgPhone}
        professionals={professionals}
        clients={clients}
        tagline={orgTagline}
        rbqNumber={orgRbqNumber}
        tpsNumber={orgTpsNumber}
        tvqNumber={orgTvqNumber}
        orgEmail={orgEmail}
        orgWebsite={orgWebsite}
        orgAddress={orgAddress}
        orgPhone={orgPhone}
        clientProjectNumber={clientProjectNumber}
        retentionRate={req?.retention_rate ?? 10}
      />
    )?.toBlob();

    return blob;
  };

  const handleApproveRequest = (req) => {
    setApprovingRequest(req);
    setApproveError('');
    setSignedPdfFile(null);
    setIsDragOver(false);
    setShowApproveModal(true);
  };

  const handleDownloadUnsignedPdf = async () => {
    if (!approvingRequest || downloadingUnsigned) return;
    setDownloadingUnsigned(true);
    try {
      const blob = await generatePaymentRequestPdfBlob(approvingRequest);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeProject = sanitizeFilename(project?.projectNumber || project?.project_number || project?.name || 'Projet');
      const today = new Date()?.toISOString()?.slice(0, 10);
      a.download = `${safeProject}_DP${approvingRequest?.number ?? '?'}_${today}.pdf`;
      document?.body?.appendChild(a);
      a?.click();
      document?.body?.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download unsigned PDF failed:', err);
    } finally {
      setDownloadingUnsigned(false);
    }
  };

  const confirmApproveRequest = async () => {
    if (!approvingRequest || approveLoading || !signedPdfFile) return;
    setApproveLoading(true);
    setApproveError('');
    try {
      // a. Upload the signed PDF provided by the user
      const today = new Date()?.toISOString()?.slice(0, 10);
      const uploadPath = `payment_request/${approvingRequest?.id}/DP${approvingRequest?.number}_signe_${today}.pdf`;
      const { error: uploadError } = await supabase?.storage
        ?.from('contracts-documents')
        ?.upload(uploadPath, signedPdfFile, { contentType: 'application/pdf', upsert: true });
      if (uploadError) throw new Error(uploadError?.message || t('paymentRequests.excelDetail.uploadFailed'));

      // b. Only if upload succeeded → update status to approved
      await paymentRequestService?.updateStatus(approvingRequest?.id, 'approved');

      // c. Close modal and refresh list
      setShowApproveModal(false);
      setApprovingRequest(null);
      setSignedPdfFile(null);
      await fetchRequests();
    } catch (err) {
      console.error('Approve request failed:', err);
      setApproveError(err?.message || t('paymentRequests.approveRequest.error'));
    } finally {
      setApproveLoading(false);
    }
  };

  const handleSaveRequest = async ({ baseLines, extraLines, status, requestId, requestNumber, periodStart, periodEnd, retention_rate }) => {
    // Safety net: never write to an approved DP
    if (status === 'approved' && requestId) {
      throw new Error(t('paymentRequests.saveBlockedApproved'));
    }
    let activeRequestId = requestId;
    let activeRequestNumber = requestNumber;

    try {
      if (!activeRequestId) {
        const newReq = await paymentRequestService?.create(projectId, status, periodStart || null, periodEnd || null, retention_rate ?? 10);
        activeRequestId = newReq?.id;
        activeRequestNumber = newReq?.number;
      } else {
        await paymentRequestService?.updateStatus(activeRequestId, status);
        await paymentRequestService?.updatePeriod(activeRequestId, periodStart || null, periodEnd || null);
        await paymentRequestService?.updateRetentionRate(activeRequestId, retention_rate ?? 10);
      }

      await paymentRequestService?.saveRequest(
        projectId,
        activeRequestId,
        activeRequestNumber,
        baseLines,
        extraLines,
        status
      );
    } finally {
      // Always re-fetch after DB operations complete (success or partial failure),
      // so a newly created request always appears in the list immediately.
      await fetchRequests();
    }
  };

  const calculateKPIs = () => {
    const totalRequests = paymentRequests?.length || 0;
    const totalValue = paymentRequests?.reduce((sum, r) => sum + (parseFloat(r?.totalAmount) || 0), 0) || 0;
    const invoiced = paymentRequests?.filter(r => r?.billing_status === 'invoiced')?.length || 0;
    const paid = paymentRequests?.filter(r => r?.billing_status === 'paid')?.length || 0;
    return { totalRequests, totalValue, invoiced, paid };
  };

  const kpis = calculateKPIs();

  const nextRequestNumber = paymentRequests?.length > 0
    ? Math.max(...paymentRequests?.map(r => r?.number)) + 1
    : 1;

  const [exportingDetailExcel, setExportingDetailExcel] = useState(false);

  const handleExportExcel = async () => {
    if (selectedIds?.size === 0 || exportingDetailExcel) return;
    setExportingDetailExcel(true);
    try {
      // Use the first selected request (in display order)
      const firstSelected = filteredRequests?.find(r => selectedIds?.has(r?.id));
      if (!firstSelected) return;

      // Fetch org info
      let companyName = '';
      let orgAddress = null;
      let orgPhone = null;
      let orgTpsNumber = null;
      let orgTvqNumber = null;
      try {
        const orgId = await getCurrentOrgId();
        if (orgId) {
          const { data: org } = await organizationService?.getOrgInfo(orgId);
          if (org) {
            companyName  = org?.name || '';
            orgAddress   = org?.address || null;
            orgPhone     = org?.phone || null;
            orgTpsNumber = org?.tps_number || null;
            orgTvqNumber = org?.tvq_number || null;
          }
        }
      } catch { /* continue without org info */ }

      let allItems = [];
      try { allItems = await paymentRequestService?.getItemsByProjectId(projectId); } catch { allItems = []; }

      const values = await paymentRequestService?.getValuesByRequestId(firstSelected?.id);

      await exportDetailToExcel({
        req: firstSelected,
        allItems,
        values,
        projectName:   project?.name || '',
        projectNumber: project?.projectNumber || project?.project_number || '',
        clientName:    project?.client_name || '—',
        companyName,
        orgAddress,
        orgPhone,
        orgTpsNumber,
        orgTvqNumber,
        language: language || 'fr',
        t,
      });
    } catch (err) {
      console.error('Excel detail export failed:', err);
    } finally {
      setExportingDetailExcel(false);
    }
  };

  const handleExportPDF = () => {
    exportPaymentRequestsToPDF(filteredRequests || [], project?.name, null, t, language);
  };

  return (
    <div className="pt-0 pb-0 px-0 h-full flex flex-col">
      {/* Header Block — matches Contracts/Change Orders style */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-6">
          {/* Left: Title + project breadcrumb + subtitle */}
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Receipt" size={20} className="text-primary" />
              <h1 className="text-lg font-semibold text-gray-900">
                {t('paymentRequests.title')}
              </h1>
            </div>

            {/* Project reference breadcrumb */}
            {project && (
              <div className="flex items-center gap-1 mb-1">
                <span
                  onClick={() => navigate('/project-detail')}
                  className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
                >
                  {project?.projectNumber || t('common.na')}
                </span>
                <span className="text-sm text-primary font-normal">-</span>
                <span
                  onClick={() => navigate('/project-detail')}
                  className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
                >
                  {project?.name || t('common.loading')}
                </span>
              </div>
            )}

            <p className="text-xs text-gray-500">
              {t('paymentRequests.subtitle')}
            </p>
          </div>

          {/* Right: KPI Cards */}
          <div className="flex-1 flex items-center justify-end gap-2">
            <StatsCard
              title={t('paymentRequests.kpi.totalRequests')}
              value={kpis?.totalRequests}
              sublabel=""
              iconName="Receipt"
              iconColor="text-blue-500"
              valueColor="text-blue-500"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('paymentRequests.kpi.totalValue')}
              value={formatCurrency(kpis?.totalValue || 0)}
              sublabel=""
              iconName="DollarSign"
              iconColor="text-green-500"
              valueColor="text-gray-900"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('paymentRequests.kpi.invoiced')}
              value={kpis?.invoiced}
              sublabel=""
              iconName="FileText"
              iconColor="text-blue-500"
              valueColor="text-blue-600"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('paymentRequests.kpi.paid')}
              value={kpis?.paid}
              sublabel=""
              iconName="CheckCircle2"
              iconColor="text-green-500"
              valueColor="text-green-600"
              titleWeight="font-medium"
            />
          </div>
        </div>
      </div>
      {/* Search + Add Button Row */}
      <div className="flex items-center justify-between gap-3 mb-4">
        {/* Search Box */}
        <div className="relative flex-1 max-w-sm">
          <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            placeholder={t('paymentRequests.searchPlaceholder')}
            className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Icon name="X" size={12} />
            </button>
          )}
        </div>

        {/* Right group: Export Buttons + Add Button */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Export Buttons — visible only when there are rows */}
          {filteredRequests?.length > 0 && (
            <>
              <button
                onClick={handleExportExcel}
                disabled={selectedIds?.size === 0 || exportingDetailExcel}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={selectedIds?.size === 0
                  ? t('paymentRequests.toolbar.selectARequest')
                  : t('paymentRequests.toolbar.exportDetailExcel', { number: filteredRequests?.find(r => selectedIds?.has(r?.id))?.number ?? '?' })
                }
              >
                {exportingDetailExcel ? (
                  <Icon name="Loader2" size={13} className="text-green-600 animate-spin flex-shrink-0" />
                ) : (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                    <rect width="24" height="24" rx="3" fill="#217346"/>
                    <path d="M13 7h7v2h-7V7zm0 4h7v2h-7v-2zm0 4h7v2h-7v-2z" fill="white"/>
                    <path d="M4 7l4 5-4 5h2.5l2.75-3.5L12 17h2.5L10 12l4.5-5H12l-2.75 3.5L6.5 7H4z" fill="white"/>
                  </svg>
                )}
                {t('paymentRequests.exportExcel')}
              </button>
              <button
                onClick={handleExportPDF}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                title={t('paymentRequests.toolbar.exportPdf')}
              >
                <Icon name="FileText" size={13} className="text-red-500" />
                PDF
              </button>
              {selectedIds?.size > 0 && (
                <button
                  onClick={handleDownloadDetailPDF}
                  disabled={downloadingDetail}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  title={t('paymentRequests.toolbar.downloadDetail')}
                >
                  {downloadingDetail
                    ? <Icon name="Loader2" size={13} className="text-orange-500 animate-spin" />
                    : <Icon name="FileDown" size={13} className="text-orange-500" />
                  }
                  {t('paymentRequests.toolbar.downloadDetailCount', { count: selectedIds?.size })}
                </button>
              )}
            </>
          )}

          <button
            onClick={handleAddRequest}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors shadow-sm"
          >
            <Icon name="Plus" size={13} />
            {t('paymentRequests.addRequest')}
          </button>
        </div>
      </div>
      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden min-h-0 flex flex-col">
        {paymentRequests?.length === 0 && !loading ? (
          <div className="p-12 text-center">
            <Icon name="Receipt" size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-500">{t('paymentRequests.empty')}</p>
          </div>
        ) : (
          <div className="min-h-0 overflow-y-auto overflow-x-auto overscroll-contain">
            {/* Payment requests table */}
            {console.log('[ClientInvoicesPage] about to render table. filteredRequests=', filteredRequests)}
            <PaymentRequestsTable
              requests={filteredRequests ?? []}
              loading={loading}
              onEdit={handleEditRequest}
              onDelete={handleDeleteRequest}
              onViewDocuments={handleViewDocuments}
              onViewRequest={handleViewRequest}
              onApproveRequest={handleApproveRequest}
              onRefresh={fetchRequests}
              selectedIds={selectedIds}
              onToggleRow={handleToggleRow}
              onToggleAll={handleToggleAll}
            />
          </div>
        )}
      </div>
      {/* View (read-only aperçu) Modal */}
      {viewingRequest && (
        <PaymentRequestViewModal
          request={viewingRequest}
          project={project}
          projectId={projectId}
          onClose={() => setViewingRequest(null)}
          onEdit={(req) => {
            setViewingRequest(null);
            handleEditRequest(req);
          }}
          onExportPDF={async (req) => {
            setViewPdfExporting(true);
            try {
              const blob = await generatePaymentRequestPdfBlob(req);
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              const safeProject = sanitizeFilename(project?.projectNumber || project?.project_number || project?.name || 'Projet');
              const today = new Date()?.toISOString()?.slice(0, 10);
              a.download = `${safeProject}_DP${req?.number ?? '?'}_${today}.pdf`;
              document?.body?.appendChild(a);
              a?.click();
              document?.body?.removeChild(a);
              URL.revokeObjectURL(url);
            } catch (err) {
              console.error('PDF export failed:', err);
            } finally {
              setViewPdfExporting(false);
            }
          }}
          pdfExporting={viewPdfExporting}
        />
      )}
      {/* Create / Edit Modal */}
      {showModal && (
        <PaymentRequestModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setEditingRequest(null); }}
          onSave={handleSaveRequest}
          projectId={projectId}
          requestId={editingRequest?.id || null}
          requestNumber={editingRequest?.number || nextRequestNumber}
          existingStatus={editingRequest?.status || 'draft'}
        />
      )}
      {/* View Documents Modal */}
      {showDocsModal && docsRequest && (
        <DocumentsModal
          isOpen={showDocsModal}
          onClose={() => { setShowDocsModal(false); setDocsRequest(null); }}
          entityId={docsRequest?.id}
          entityType="payment_request"
          subtitle={`DP${docsRequest?.number}`}
          onFilesChanged={() => fetchRequests()}
        />
      )}
      {/* Edit Blocked Modal (approved DP) */}
      {showEditBlocked && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowEditBlocked(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Icon name="Lock" size={18} className="text-orange-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">{t('paymentRequests.closedReadOnly')}</h3>
              </div>
            </div>
            <p className="text-xs text-gray-600 mb-5">{editBlockedMessage}</p>
            <div className="flex items-center justify-end">
              <button
                onClick={() => setShowEditBlocked(false)}
                className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Blocked Modal */}
      {showDeleteBlocked && blockedDeleteRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowDeleteBlocked(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={18} className="text-orange-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">{t('paymentRequests.deleteBlockedTitle')}</h3>
                <p className="text-xs text-gray-500 mt-0.5">DP{blockedDeleteRequest?.number}</p>
              </div>
            </div>
            <p className="text-xs text-gray-600 mb-5">
              {deleteBlockedMessage}
            </p>
            <div className="flex items-center justify-end">
              <button
                onClick={() => setShowDeleteBlocked(false)}
                className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation */}
      {showDeleteConfirm && deletingRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => !deleteLoading && (setShowDeleteConfirm(false), setDeleteError(''))} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="Trash2" size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">{t('paymentRequests.deleteConfirmTitle', { number: deletingRequest?.number })}</h3>
              </div>
            </div>
            <p className="text-xs text-gray-600 mb-5">{t('paymentRequests.deleteConfirmMessage')}</p>
            {deleteError && <p className="text-xs text-red-500 mb-4">{deleteError}</p>}
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(''); }}
                disabled={deleteLoading}
                className="px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteLoading}
                className="px-4 py-2 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {deleteLoading && <Icon name="Loader2" size={13} className="animate-spin" />}
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Approve Request Modal */}
      {showApproveModal && approvingRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => !approveLoading && (setShowApproveModal(false), setApproveError(''), setSignedPdfFile(null))} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Icon name="CheckCircle2" size={18} className="text-green-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {t('paymentRequests.approveRequest.title', { number: approvingRequest?.number })}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">DP{approvingRequest?.number}</p>
              </div>
            </div>

            {/* Optional: Download unsigned PDF */}
            <div className="mb-4">
              <button
                type="button"
                onClick={handleDownloadUnsignedPdf}
                disabled={downloadingUnsigned || approveLoading}
                className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 underline underline-offset-2 disabled:opacity-50 transition-colors"
              >
                {downloadingUnsigned
                  ? <Icon name="Loader2" size={12} className="animate-spin" />
                  : <Icon name="Download" size={12} />}
                {t('paymentRequests.approveRequest.downloadUnsigned')}
              </button>
            </div>

            {/* Drag-and-drop zone for signed PDF */}
            <div
              onDragOver={(e) => { e?.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => {
                e?.preventDefault();
                setIsDragOver(false);
                const file = e?.dataTransfer?.files?.[0];
                if (file?.type === 'application/pdf') {
                  setSignedPdfFile(file);
                  setApproveError('');
                } else if (file) {
                  setApproveError(t('paymentRequests.approveRequest.pdfOnly'));
                }
              }}
              onClick={() => {
                if (approveLoading) return;
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'application/pdf';
                input.onchange = (e) => {
                  const file = e?.target?.files?.[0];
                  if (file) { setSignedPdfFile(file); setApproveError(''); }
                };
                input?.click();
              }}
              className={`mb-4 border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors ${
                isDragOver
                  ? 'border-green-400 bg-green-50'
                  : signedPdfFile
                  ? 'border-green-400 bg-green-50' :'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
              }`}
            >
              {signedPdfFile ? (
                <div className="flex items-center justify-center gap-2">
                  <Icon name="FileCheck" size={16} className="text-green-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-green-700 truncate max-w-xs">{signedPdfFile?.name}</span>
                  <button
                    type="button"
                    onClick={(e) => { e?.stopPropagation(); setSignedPdfFile(null); }}
                    className="ml-1 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Icon name="X" size={13} />
                  </button>
                </div>
              ) : (
                <div>
                  <Icon name="Upload" size={20} className="mx-auto mb-1.5 text-gray-400" />
                  <p className="text-xs font-medium text-gray-600">{t('paymentRequests.approveRequest.dropZoneLabel')}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{t('paymentRequests.approveRequest.dropZoneHint')}</p>
                </div>
              )}
            </div>

            {approveError && (
              <p className="text-xs text-red-500 mb-4">{approveError}</p>
            )}
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => { setShowApproveModal(false); setApproveError(''); setSignedPdfFile(null); }}
                disabled={approveLoading}
                className="px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmApproveRequest}
                disabled={approveLoading || !signedPdfFile}
                className="px-4 py-2 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {approveLoading && <Icon name="Loader2" size={13} className="animate-spin" />}
                {t('paymentRequests.approveRequest.approve')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Blocked Create Modal */}
      {showBlockedCreate && blockedByRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowBlockedCreate(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertCircle" size={18} className="text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">{t('paymentRequests.blockedCreate.title')}</h3>
                <p className="text-xs text-gray-500 mt-0.5">DP{blockedByRequest?.number}</p>
              </div>
            </div>
            <p className="text-xs text-gray-600 mb-5">
              {t('paymentRequests.blockedCreate.message', { number: blockedByRequest?.number })}
            </p>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setShowBlockedCreate(false)}
                className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
              >
                {t('paymentRequests.blockedCreate.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientInvoicesPage;